import React, { useEffect, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import moment from 'moment';
import { SEND_TEST_EMAIL, SEND_TEST_PUSH, CREATE_MARKETING_CAMPAIGN } from "utils/mutations";
import { useMutation, useApolloClient } from "@apollo/react-hooks";
import { useHistory } from "react-router-dom";
import {
  ExpansionPanel as Panel,
  ExpansionPanelSummary as Summary,
  ExpansionPanelDetails as Details,
} from "@material-ui/core/";
import { useCommsBatching, replaceVariables, readSomeLines } from "utils";
import { Loading } from "components/common";
import { addTextContentVariables, clearBatch } from "state/reducers/communicationBatching";
import SMSPhoneInput from "./utils/components/sms/SMSPhoneInput";
import TestEmailInput from "./utils/components/TestEmailInput";
import SMSChatBubblePreview from "./utils/components/sms/SMSChatBubblePreview";
import MarketingNavigation from "./utils/components/MarketingNavigation";
import MarketingStepper from "./utils/components/MarketingStepper";
import PushChatBubblePreview from "./utils/components/push/PushChatBubblePreview";
import MarketingButtons from "./utils/components/MarketingButtons";
import CSVUpload from "./utils/components/CSVUpload";
import { streamUploadFile, RETRIEVE_RECIPIENTS_UPLOAD_LINK } from './marketingLanding';

const guessedTz = moment.tz.guess();

const BatchReview = () => {
  const history = useHistory();
  const dispatch = useDispatch();
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(false);
  const [success, setSuccess] = useState(null);
  const [testNumber, setTestNumber] = useState();
  const [testEmail, setTestEmail] = useState();
  const batchData = useSelector((state) => state.communicationBatching);
  const [sampleDataIdx, setSampleDataIdx] = useState(0);
  const [totalRows, setTotalRows] = useState(0);
  const [totalRowsLoading, setTotalRowsLoading] = useState(false);
  const [sendTestEmail] = useMutation(SEND_TEST_EMAIL);
  const [sendTestPushNotification] = useMutation(SEND_TEST_PUSH);
  const [sendCreateMarketingCampaign] = useMutation(CREATE_MARKETING_CAMPAIGN);
  const client = useApolloClient();

  const { getPreviousLink } = useCommsBatching({ currentPage: "review" });
  const {
    batchId,
    fileName,
    fileSamples,
    emailSender,
    emailTemplateId,
    emailTemplateVariables,
    textContent,
    textContentVariables,
    pushContent,
    pushContentHeader,
    pushContentVariables,
    communicationTypes,
    html,
    missingColumns,
    batchName,
    scheduledTime,
    recurringSchedule,
    columnNames,
    pushContentData,
  } = batchData;

  const { text, push, email } = communicationTypes;

  if (!batchId) {
    dispatch(clearBatch());
    history.push('/v2-marketing');
  }

  const updateSample = () => {
    setSampleDataIdx((sampleDataIdx + 1) % fileSamples.length);
  };

  useEffect(() => {
    const countCSVRows = async () => {
      setTotalRowsLoading(true);
      try {
        const count = await readSomeLines(fileName, Infinity, true);
        setTotalRows(count - 1);
        setTotalRowsLoading(false);
      } catch (err) {
        setTotalRowsLoading(false);
      }
    };

    countCSVRows();
  }, [fileName]);

  const Icon = [<i key="icon" className="fas fa-ellipsis-h text-black" />];

  const handleDiscardChanges = () => {
    dispatch(clearBatch());
    history.push("/v2-marketing");
  };

  const handleSubmit = async () => {
    setLoading(true);
    try {
      // first upload file to S3

      const { data: { retrieveRecipientsUploadLink } }
      = await client.query({
        fetchPolicy: 'network-only',
        query: RETRIEVE_RECIPIENTS_UPLOAD_LINK,
        variables: {
          uuid: batchId,
        },
      });
      const { url } = retrieveRecipientsUploadLink;

      await streamUploadFile(url, fileName);

      const { data } = await sendCreateMarketingCampaign({
        variables: {
          uuid: batchId,
          ...(email && {
            email_template_id: emailTemplateId,
            email_template_variables: emailTemplateVariables,
            sender: emailSender,
          }),
          ...(text && {
            text_content: textContent,
            text_content_variables: textContentVariables,
          }),
          ...(push && {
            push_content: pushContent,
            push_content_header: pushContentHeader,
            push_content_variables: pushContentVariables,
            push_content_data: JSON.stringify(pushContentData),
          }),
          scheduled_time: scheduledTime,
          batch_name: batchName,
          cron_schedule: recurringSchedule ? recurringSchedule.value : null,
          column_names: columnNames,
        },
      });

      // were done creating.
      // lets clear the redux state and go back to main.
      dispatch(clearBatch());
      history.push("/v2-marketing");
      setLoading(false);
    } catch (err) {
      console.error(err);
      setLoading(false);
    }
  };

  const handleTestEmail = async () => {
    setLoading(true);
    try {
      const { data } = sendTestEmail({
        variables: {
          data: JSON.stringify({
            ...fileSamples[0],
            email: testEmail,
          }),
          templateId: emailTemplateId,
          sender: emailSender,
        },
      });
      const { status, message } = data.sendTestEmail;
      if (status === "OK") {
        console.log("test email sent");
      } else {
        console.log("error - ", message);
        setError(message);
      }
      setLoading(false);
    } catch (err) {
      console.error(error);
      setError(err);
      setLoading(false);
    }
  };

  const handleTestPush = async () => {
    if (!pushContent || ! pushContentHeader || !testEmail) {
      setLoading(false);
      setSuccess(false);
      setError("invalid number");
    } else {
      const payload = {
        title: replaceVariables(pushContentHeader, pushContentVariables, fileSamples[0]),
        body: replaceVariables(pushContent, pushContentVariables, fileSamples[0]),
        email: testEmail,
      };
      await sendTestPushNotification({ variables: payload }).then((response) => {
        console.log(response.data.sendTestPushNotification.status);
        if (response.data.sendTestPushNotification.status === `SUCCESS`) {
          setSuccess(true);
          setLoading(false);
        }
        setError(true);
        console.log("ERROR: sending test notification");
        setLoading(false);
      });
    }
  };

  return (
    <div className="marketing-container flex flex-col bg-transparent mb-4">
      <MarketingNavigation/>
      <MarketingStepper />
      <MarketingButtons
        spaceBetween
        discardButton="Discard"
        discardChanges={handleDiscardChanges}
        prevLink={getPreviousLink()}
        prevButton="Previous"
        required={loading || ((text && !textContent) || !fileName || (push && (!pushContent || !pushContentHeader)) || (email && !emailTemplateId))}
        submit={handleSubmit}
        submitButton="Submit"
        success={success}
        loading={loading}
        error={error}
      />
      {loading && (<Loading/>)}
      <p className="text-3xl text-center mx-auto my-10">
        Review and Submit Batch :
      </p>
      <p className="text-2xl text-left mx-auto my-1">
      Batch Name: {batchName}
      </p>
      <p className="text-2xl text-left mx-auto my-1">
      ScheduledTime: {
          moment(scheduledTime).isBefore(moment())
            ? 'ASAP'
            : moment.tz(scheduledTime, "UTC").tz(guessedTz).format("MM-DD-YYYY hh:mm:ss zz")
        }
      </p>
      <p className="text-2xl text-left mx-auto my-1">
      Recurring Schedule: { recurringSchedule ? recurringSchedule.name : 'Not Recurring' }
      </p>
      <div className="flex flex-col justify-center rounded-lg review-panel">
        <Panel>
          <Summary expandIcon={Icon} aria-label="Expand">
            Batch Information Overview
          </Summary>
          <Details>
            { communicationTypes.email && (
              <div className="pr-4" style={{ maxWidth: "50%", minWidth: "33%" }}>
                <div>
                  <p className="font-bold text-green-500">Email Information</p>
                </div>
                <div>
                  <p className="font-bold">Sender</p>
                  <p>{emailSender}</p>
                </div>
                <div className="mt-2">
                  <p className="font-semibold">Template</p>
                  <p>{`(${emailTemplateId})`}</p>
                </div>
                <div className="mt-2">
                  <p className="font-semibold">Template variables</p>
                  <p>{emailTemplateVariables.length ? emailTemplateVariables.join(", ") : "none"}</p>
                </div>
                <div className="mt-2">
                  <p className="font-semibold">Missing columns</p>
                  <p>
                    {email.length ? missingColumns.join(", ") : "none"}
                  </p>
                </div>
                <div className="mt-2">
                  <p className="font-semibold">Number of emails to be sent</p>
                  <p>{ totalRowsLoading ? '..loading' : totalRows }</p>
                </div>
              </div>
            )}
            { communicationTypes.text && (
              <div className="pr-4" style={{ maxWidth: "50%", minWidth: "33%" }}>
                <div>
                  <p className="font-bold text-green-500 ">Text Message Information</p>
                </div>
                <div>
                  <p className="font-bold">text message Content</p>
                  <p>{textContent}</p>
                </div>
                <div className="mt-2">
                  <p className="font-semibold">Text Template variables</p>
                  <p>{textContentVariables.length ? textContentVariables.join(", ") : "none"}</p>
                </div>
                <div className="mt-2">
                  <p className="font-semibold">Missing columns</p>
                  <p>
                    {email.length ? missingColumns.join(", ") : "none"}
                  </p>
                </div>
                <div className="mt-2">
                  <p className="font-semibold">Number of text messages to be sent</p>
                  <p>{ totalRowsLoading ? '..loading' : totalRows }</p>
                </div>
              </div>
            )}
            { communicationTypes.push && (
              <div className="pr-4" style={{ maxWidth: "50%", minWidth: "33%" }}>
                <div>
                  <p className="font-bold text-green-500">Push Notification</p>
                </div>
                <div>
                  <p className="font-bold">push notification header</p>
                  <p>{pushContentHeader}</p>
                </div>
                <div>
                  <p className="font-bold">push notification Content</p>
                  <p>{pushContent}</p>
                </div>
                <div className="mt-2">
                  <p className="font-semibold">push template variables</p>
                  <p>{pushContentVariables.length ? pushContentVariables.join(", ") : "none"}</p>
                </div>
                <div className="mt-2">
                  <p className="font-semibold">Missing columns</p>
                  <p>
                    {email.length ? missingColumns.join(", ") : "none"}
                  </p>
                </div>
                <div className="mt-2">
                  <p className="font-semibold">Number of push notifications to be sent</p>
                  <p>{ totalRowsLoading ? '..loading' : totalRows }</p>
                </div>
              </div>
            )}
          </Details>
        </Panel>
        <Panel>
          <Summary expandIcon={Icon} aria-label="Expand">
            Batch Preview
          </Summary>
          <Details>
            {communicationTypes.email && (
              <Panel>
                <Summary expandIcon={Icon} aria-label="Expand">
                  Preview email template
                  <small className="text-green-500 ml-2 my-auto">recommended</small>
                </Summary>
                <Details>
                  <div
                    dangerouslySetInnerHTML={{
                      __html: replaceVariables(html, emailTemplateVariables, fileSamples[sampleDataIdx]),
                    }}
                  />
                  <span className="mx-auto text-center py-3">
                    <button
                      type="button"
                      className="focus:outline-none focus:border-none active:outline-none active:border-none"
                      onClick={() => updateSample() }
                    >
                      <small className="text-green-500">
                        Randomize Sample
                        <i className="fas fa-edit mx-1" />
                      </small>
                    </button>
                  </span>
                </Details>
              </Panel>
            )}
          </Details>
          <Details>
            {communicationTypes.text && (
              <Panel>
                <Summary expandIcon={Icon} aria-label="Expand">
                  Preview SMS
                  <small className="text-green-500 ml-2 my-auto">recommended</small>
                </Summary>
                <Details>
                  <SMSChatBubblePreview setError={setError} key="chat_preview" />
                </Details>
              </Panel>
            )}
          </Details>
          <Details>
            {communicationTypes.push && (
              <Panel>
                <Summary expandIcon={Icon} aria-label="Expand">
                  Preview Push
                  <small className="text-green-500 ml-2 my-auto">recommended</small>
                </Summary>
                <Details>
                  <div className="flex flex-col justify-center mx-auto my-auto bg-transparent rounded-lg">
                    <PushChatBubblePreview setError={setError} error={error} />
                  </div>
                  {/* <SMSChatBubblePreview setError={setError} key="chat_preview" />
                  {error && <small>{error}</small>} */}
                </Details>
              </Panel>
            )}
          </Details>
        </Panel>
        <Panel>
          <Summary expandIcon={Icon} aria-label="Expand">
            Audience
          </Summary>
          <Details>
            <CSVUpload />
            {totalRowsLoading ? (
              <div className="text-center mx-auto mt-5">
                <i className="fas fa-user mr-2" />
                <Loading/>
              </div>
            ) : (
              <div className="text-center mx-auto mt-5">
                <i className="fas fa-user mr-2" />
                {totalRows || "0"}
              </div>
            )
            }
          </Details>
        </Panel>
        <Panel>
          <Summary expandIcon={Icon} aria-label="Expand">
            Test Preview
          </Summary>
          { communicationTypes.text && (
            <Panel>
              <Summary expandIcon={Icon} aria-label="Expand">
                Send Test SMS
                <small className="text-green-500 ml-2 my-auto">recommended</small>
              </Summary>
              <Details>
                <SMSPhoneInput
                  key="phone_input"
                  testNumber={testNumber}
                  setTestNumber={setTestNumber}
                />
              </Details>
            </Panel>
          )}
          { communicationTypes.email && (
            <Panel>
              <Summary expandIcon={Icon} aria-label="Expand">
                Send Test Email
                <small className="text-green-500 ml-2 my-auto">recommended</small>
              </Summary>
              <Details>
                <TestEmailInput
                  setTestEmail={setTestEmail}
                  handleSubmit={handleTestEmail}
                />
              </Details>
            </Panel>
          )}
          { communicationTypes.push && (
            <Panel>
              <Summary expandIcon={Icon} aria-label="Expand">
                Send Test Push Notification
                <small className="text-green-500 ml-2 my-auto">recommended</small>
              </Summary>
              <Details>
                <TestEmailInput
                  setTestEmail={setTestEmail}
                  handleSubmit={handleTestPush}
                />
              </Details>
            </Panel>
          )}
        </Panel>
      </div>
    </div>
  );
};

export default BatchReview;
